// Database and Sync Engine for BPA HTML Vault - Repaired sync logic
const DB_NAME = 'BPA_HTML_Vault';
const STORE_NAME = 'pages';
const DB_VERSION = 1;
const DEFAULT_CATEGORIES = ['Education', 'Business', 'Operations', 'Technical', 'Forms', 'Other'];
const REQUEST_TIMEOUT_MS = 20000;

// Helper to safely parse timestamps from various formats (including Google Sheets date objects)
function parseUpdatedAt(value) {
  if (typeof value === 'number' && !isNaN(value)) return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (!isNaN(parsed)) return parsed;
  }
  if (value && typeof value === 'object' && value.getTime) {
    try {
      const t = value.getTime();
      if (!isNaN(t)) return t;
    } catch (e) { /* ignore */ }
  }
  if (value === null || value === undefined || value === '') return 0;
  return 0;
}

// Helper to create a fetch with timeout using AbortController
function fetchWithTimeout(url, payload, options = {}) {
  const timeoutMs = options.timeoutMs || REQUEST_TIMEOUT_MS;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify(payload),
    signal: controller.signal,
    mode: options.mode || 'cors',
    credentials: options.credentials || 'omit',
    cache: options.cache || 'no-store',
    redirect: options.redirect || 'follow',
    referrerPolicy: options.referrerPolicy || 'no-referrer'
  }).finally(() => clearTimeout(timeoutId));
}

function normalizeCategoriesFromBackend(value) {
  if (!value) return null;
  let parsed = value;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch (e) { /* try next */ }
  }
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch (e) { /* give up */ }
  }
  let cats = [];
  let updatedAt = 0;
  if (Array.isArray(parsed)) {
    cats = parsed;
  } else if (parsed && typeof parsed === 'object') {
    cats = parsed.items || parsed.categories || [];
    updatedAt = parsed.updatedAt || 0;
  }
  if (!Array.isArray(cats)) return null;
  cats = cats.map(c => typeof c === 'string' ? c : String(c));
  return { categories: cats, updatedAt };
}

export const dbAPI = {
  async getAll() {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.getAll();
        request.onsuccess = () => {
          let data = request.result || [];
          resolve(data);
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.warn("DB Diagnostics: IndexedDB failed, falling back to localStorage", error);
      const localData = JSON.parse(localStorage.getItem(DB_NAME) || '[]');
      return localData;
    }
  },

  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      request.onupgradeneeded = (e) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };
    });
  },

  async save(page) {
    page.updatedAt = Date.now();
    if (!page.syncState) {
      page.syncState = 'pending';
      page.syncError = '';
      page.lastSyncAttemptAt = 0;
      page.lastSyncedAt = 0;
    }
    try {
      const db = await this.initDB();
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const putRequest = store.put(page);
      return new Promise((resolve, reject) => {
        transaction.oncomplete = async () => {
          await this.backupToLocal();
          resolve(page);
        };
        transaction.onerror = () => reject(transaction.error);
        putRequest.onerror = () => reject(putRequest.error);
      });
    } catch (error) {
      console.error("Save Diagnostics: IndexedDB save failed", error);
      throw error;
    }
  },

  async softDelete(id) {
    try {
      const db = await this.initDB();
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(id);
      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const page = request.result;
          if (page) {
            page.deletedAt = Date.now();
            page.updatedAt = Date.now();
            page.syncState = 'pending';
            page.syncError = '';
            const putRequest = store.put(page);
            transaction.oncomplete = async () => {
              await this.backupToLocal();
              resolve(page);
            };
            transaction.onerror = () => reject(transaction.error);
            putRequest.onerror = () => reject(putRequest.error);
          } else {
            reject('Page not found');
          }
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error("Delete Diagnostics: Soft delete failed", error);
      throw error;
    }
  },

  async validatePasscode(syncUrl, passcode) {
    if (!syncUrl) return { success: false, code: 'NETWORK_ERROR', message: 'Sync URL not configured' };

    const maxAttempts = 2;
    let attempt = 0;

    while (attempt < maxAttempts) {
      attempt++;
      console.log(`[Auth] Attempt ${attempt}/${maxAttempts} on hostname: ${window.location.hostname}`);

      try {
        const response = await fetchWithTimeout(syncUrl, {
          action: 'validatePasscode',
          passcode
        }, {
          timeoutMs: 30000,
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          redirect: 'follow',
          referrerPolicy: 'no-referrer'
        });

        const contentType = response.headers.get('content-type') || '';
        console.log('[Auth] HTTP status:', response.status, '| Content-Type:', contentType);

        if (!response.ok) {
          if ([429, 502, 503, 504].includes(response.status) && attempt < maxAttempts) {
            console.warn('[Auth] HTTP ' + response.status + ' - retrying in 1s');
            await new Promise(r => setTimeout(r, 1000));
            continue;
          }
          throw new Error(`HTTP error: ${response.status}`);
        }

        const responseText = await response.text();

        if (!responseText || responseText.trim() === '') {
          if (attempt < maxAttempts) {
            console.warn('[Auth] Empty response - retrying in 1s');
            await new Promise(r => setTimeout(r, 1000));
            continue;
          }
          return {
            success: false,
            code: 'INVALID_RESPONSE',
            message: 'The authentication service returned an invalid response. Please try again.'
          };
        }

        if (responseText.trim().startsWith('<')) {
          if (attempt < maxAttempts) {
            console.warn('[Auth] HTML response detected - retrying in 1s');
            await new Promise(r => setTimeout(r, 1000));
            continue;
          }
          return {
            success: false,
            code: 'INVALID_RESPONSE',
            message: 'The authentication service returned an invalid response. Please try again.'
          };
        }

        let result;
        try {
          result = JSON.parse(responseText);
        } catch (parseError) {
          console.error('[Auth] JSON parse failed:', parseError.message);
          if (attempt < maxAttempts) {
            console.warn('[Auth] Malformed response - retrying in 1s');
            await new Promise(r => setTimeout(r, 1000));
            continue;
          }
          return {
            success: false,
            code: 'INVALID_RESPONSE',
            message: 'The authentication service returned an invalid response. Please try again.'
          };
        }

        console.log('[Auth] Response code:', result.code, '| ok:', result.ok, '| valid:', result.valid);

        if (result.code === 'BACKEND_NOT_READY') {
          return { success: false, code: 'BACKEND_NOT_READY', message: 'The vault could not connect to its data store.' };
        }

        if (result.code === 'INVALID_PASSCODE') {
          return { success: false, code: 'INVALID_PASSCODE', message: 'Incorrect passcode. Please try again.' };
        }

        if (result.code === 'INTERNAL_ERROR' || result.code === 'UNKNOWN_ACTION') {
          return { success: false, code: 'INVALID_RESPONSE', message: 'The authentication service returned an invalid response. Please try again.' };
        }

        const accessGranted =
          result &&
          result.ok === true &&
          result.valid === true &&
          (
            result.code === 'ACCESS_GRANTED' ||
            result.code === undefined ||
            result.code === null ||
            result.code === ''
          );

        if (accessGranted) {
          const formatType = result.code === 'ACCESS_GRANTED' ? 'current' : 'legacy';
          console.log('[Auth] Login successful - matched', formatType, 'response format');
          sessionStorage.setItem('BPA_SESSION_PASSCODE', passcode);
          sessionStorage.setItem('BPA_SESSION_EXPIRY', Date.now() + (30 * 60 * 1000));
          return { success: true, message: result.message || 'Passcode validated' };
        }

        if (result.ok === true && result.valid === false) {
          console.log('[Auth] Passcode rejected by backend');
          return { success: false, code: 'INVALID_PASSCODE', message: 'Incorrect passcode. Please try again.' };
        }

        console.error('[Auth] Unrecognized response format');
        return {
          success: false,
          code: 'INVALID_RESPONSE',
          message: 'The authentication service returned an invalid response. Please try again.'
        };

      } catch (error) {
        if (error.name === 'AbortError') {
          console.error('[Auth] Request timed out');
          if (attempt < maxAttempts) {
            console.warn('[Auth] Timeout - retrying in 1s');
            await new Promise(r => setTimeout(r, 1000));
            continue;
          }
          return { success: false, code: 'NETWORK_ERROR', message: 'The authentication service took too long to respond. Please try again.' };
        }
        console.error('[Auth] Network error:', error.message);
        if (attempt < maxAttempts) {
          console.warn('[Auth] Network error - retrying in 1s');
          await new Promise(r => setTimeout(r, 1000));
          continue;
        }
        return { success: false, code: 'NETWORK_ERROR', message: 'The authentication service took too long to respond. Please try again.' };
      }
    }
  },

  backupToLocal() {
    return new Promise((resolve, reject) => {
      this.initDB().then(db => {
        const transaction = db.transaction(STORE_NAME, 'readonly');
        const request = transaction.objectStore(STORE_NAME).getAll();
        request.onsuccess = () => {
          localStorage.setItem(DB_NAME, JSON.stringify(request.result || []));
          resolve();
        };
        request.onerror = () => reject(request.error);
      }).catch(reject);
    });
  },

  async loadSettings(syncUrl) {
    if (!syncUrl) return { success: false, message: 'No sync URL' };
    try {
      const response = await fetchWithTimeout(syncUrl, {
        action: 'loadSettings',
        passcode: sessionStorage.getItem('BPA_SESSION_PASSCODE') || ''
      });
      if (!response.ok) throw new Error('loadSettings failed');
      const result = await response.json();
      const success = result.success === true || result.ok === true;
      if (!success) {
        if (result.code === 'INVALID_PASSCODE') return { success: false, code: 'SESSION_REQUIRED', message: 'Session expired' };
        return { success: false, message: result.message || 'Failed' };
      }
      const settings = result.settings || result.data || {};
      let categories = null;
      let updatedAt = 0;
      if (settings.approvedCategoriesJson) {
        const norm = normalizeCategoriesFromBackend(settings.approvedCategoriesJson);
        if (norm && norm.categories && norm.categories.length > 0) {
          categories = norm.categories;
          updatedAt = norm.updatedAt;
          localStorage.setItem('BPA_CATEGORIES', JSON.stringify(categories));
          if (updatedAt) localStorage.setItem('BPA_CATEGORIES_UPDATED_AT', updatedAt.toString());
        }
      }
      return { success: true, settings, categories, updatedAt };
    } catch (error) {
      if (error.name === 'AbortError') return { success: false, code: 'NETWORK_ERROR', message: 'Request timed out' };
      return { success: false, code: 'NETWORK_ERROR', message: error.message };
    }
  },

  async saveSetting(syncUrl, settingKey, settingValue, passcode) {
    if (!syncUrl) return { success: false, message: 'No sync URL' };
    if (!passcode) return { success: false, code: 'SESSION_REQUIRED', message: 'No active session' };
    try {
      const payloadValue = typeof settingValue === 'string' ? settingValue : JSON.stringify(settingValue);
      const response = await fetchWithTimeout(syncUrl, {
        action: 'saveSetting',
        passcode,
        settingKey,
        settingValue: payloadValue
      });
      if (!response.ok) throw new Error('saveSetting failed');
      const result = await response.json();
      return { success: result.success === true || result.ok === true };
    } catch (error) {
      if (error.name === 'AbortError') return { success: false, code: 'NETWORK_ERROR', message: 'Request timed out' };
      return { success: false, code: 'NETWORK_ERROR', message: error.message };
    }
  },

  async syncWithRemote(syncUrl) {
    if (!syncUrl) return { status: 'offline', message: 'No sync URL' };

    // Verify session before syncing
    const sessionPasscode = sessionStorage.getItem('BPA_SESSION_PASSCODE');
    const sessionExpiry = sessionStorage.getItem('BPA_SESSION_EXPIRY');
    if (!sessionPasscode || !sessionExpiry || Date.now() >= parseInt(sessionExpiry, 10)) {
      return { status: 'error', code: 'SESSION_REQUIRED', message: 'Your session expired. Please log in again.' };
    }

    console.log('[Sync] Starting sync process...');
    let pulledCount = 0;
    let pushedCount = 0;
    let verifiedCount = 0;
    let failedRecords = [];

    try {
      // --- PULL PHASE ---
      console.log('[Sync] Beginning pull...');
      const pullResponse = await fetchWithTimeout(syncUrl, {
        action: 'pull',
        passcode: sessionPasscode
      });

      if (!pullResponse.ok) {
        console.error('[Sync] Pull failed - HTTP error', pullResponse.status);
        if (pullResponse.status >= 500) return { status: 'error', code: 'SYNC_PULL_FAILED', message: 'Server error during pull. Will retry.' };
        throw new Error(`Pull failed with status: ${pullResponse.status}`);
      }

      const pullData = await pullResponse.json();
      console.log('[Sync] Pull response received');

      if (pullData.ok === false || pullData.success === false) {
        console.error('[Sync] Pull failed - backend error:', pullData.error || pullData.message);
        if (pullData.code === 'INVALID_PASSCODE') return { status: 'error', code: 'SESSION_REQUIRED', message: 'Your session expired. Please log in again.' };
        throw new Error(pullData.error || pullData.message || 'Pull validation failed');
      }

      let remoteData = [];
      if (Array.isArray(pullData)) {
        remoteData = pullData;
      } else if (pullData && (pullData.success === true || pullData.ok === true)) {
        if (Array.isArray(pullData.data)) remoteData = pullData.data;
        else if (Array.isArray(pullData.records)) remoteData = pullData.records;
        else if (Array.isArray(pullData.pages)) remoteData = pullData.pages;
      }

      if (!Array.isArray(remoteData)) {
        console.error('[Sync] Pull failed - no valid records array found in response');
        throw new Error('Invalid pull response format - no records array');
      }

      pulledCount = remoteData.length;
      console.log(`[Sync] Pull successful - ${pulledCount} records received`);

      // Get local data for comparison
      const db = await this.initDB();
      const localData = await new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME);
        const req = tx.objectStore(STORE_NAME).getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
      });

      console.log(`[Sync] Local record count before merge: ${localData.length}`);

      // Merge remote records into IndexedDB
      const mergeTx = db.transaction(STORE_NAME, 'readwrite');
      const store = mergeTx.objectStore(STORE_NAME);
      let needsPush = false;
      const localNeedsPushIds = new Set();

      // Process each remote record
      for (const remote of remoteData) {
        if (!remote || !remote.id) {
          console.warn('[Sync] Skipping invalid remote record:', remote);
          continue;
        }
        const local = localData.find(l => l.id === remote.id);
        const remoteUpdatedAt = parseUpdatedAt(remote.updatedAt);
        const localUpdatedAt = local ? parseUpdatedAt(local.updatedAt) : 0;

        if (!local) {
          // Remote record doesn't exist locally - save it
          remote.syncState = 'synced';
          remote.lastSyncedAt = Date.now();
          remote.syncError = '';
          store.put(remote);
          console.log(`[Sync] Added new remote record: ${remote.id}`);
        } else if (remoteUpdatedAt > localUpdatedAt) {
          // Remote is newer - save it
          remote.syncState = 'synced';
          remote.lastSyncedAt = Date.now();
          remote.syncError = '';
          store.put(remote);
          console.log(`[Sync] Updated record from remote (remote newer): ${remote.id}`);
        } else if (localUpdatedAt > remoteUpdatedAt) {
          // Local is newer - mark for push, do NOT overwrite local
          needsPush = true;
          localNeedsPushIds.add(local.id);
          console.log(`[Sync] Local record is newer, will push: ${local.id}`);
        }
        // If timestamps are equal, no action needed
      }

      // Check for local records that don't exist remotely
      for (const local of localData) {
        if (!remoteData.find(r => r.id === local.id)) {
          needsPush = true;
          localNeedsPushIds.add(local.id);
          console.log(`[Sync] Local record missing in remote, will push: ${local.id}`);
        }
      }

      // Wait for merge transaction to complete
      await new Promise((resolve, reject) => {
        mergeTx.oncomplete = resolve;
        mergeTx.onerror = () => reject(mergeTx.error);
      });

      console.log(`[Sync] Merge complete. Needs push: ${needsPush}, records to push: ${localNeedsPushIds.size}`);

      // --- PUSH PHASE (if needed) ---
      if (needsPush) {
        // Re-verify session before push
        const pushPasscode = sessionStorage.getItem('BPA_SESSION_PASSCODE');
        const pushExpiry = sessionStorage.getItem('BPA_SESSION_EXPIRY');
        if (!pushPasscode || !pushExpiry || Date.now() >= parseInt(pushExpiry, 10)) {
          console.warn('[Sync] Cannot push - session expired');
          return { status: 'error', code: 'SESSION_REQUIRED', message: 'Your session expired. Please log in again.' };
        }

        console.log('[Sync] Beginning push...');

        // Only push records that need uploading
        const recordsToPush = localData.filter(l => localNeedsPushIds.has(l.id));

        // Mark records as syncing
        const pushTx = db.transaction(STORE_NAME, 'readwrite');
        const pushStore = pushTx.objectStore(STORE_NAME);
        for (const record of recordsToPush) {
          record.syncState = 'syncing';
          record.lastSyncAttemptAt = Date.now();
          pushStore.put(record);
        }
        await new Promise((resolve, reject) => {
          pushTx.oncomplete = resolve;
          pushTx.onerror = () => reject(pushTx.error);
        });

        // Push to backend
        const pushResponse = await fetchWithTimeout(syncUrl, {
          action: 'push',
          passcode: pushPasscode,
          records: recordsToPush
        });

        if (!pushResponse.ok) {
          console.error('[Sync] Push failed - HTTP error', pushResponse.status);
          if (pushResponse.status >= 500) return { status: 'error', code: 'SYNC_PUSH_FAILED', message: 'Server error during push. Will retry.' };
          throw new Error(`Push failed with status: ${pushResponse.status}`);
        }

        const pushData = await pushResponse.json();
        console.log('[Sync] Push response received');

        if (pushData.ok === false || pushData.success === false) {
          console.error('[Sync] Push failed - backend error:', pushData.error || pushData.message);
          if (pushData.code === 'INVALID_PASSCODE') return { status: 'error', code: 'SESSION_REQUIRED', message: 'Your session expired. Please log in again.' };

          // Mark failed records
          const errorTx = db.transaction(STORE_NAME, 'readwrite');
          const errorStore = errorTx.objectStore(STORE_NAME);
          for (const record of recordsToPush) {
            const rec = await new Promise((resolve, reject) => {
              const req = errorStore.get(record.id);
              req.onsuccess = () => resolve(req.result);
              req.onerror = () => reject(req.error);
            });
            if (rec) {
              rec.syncState = 'error';
              rec.syncError = pushData.error || pushData.message || 'Push failed';
              errorStore.put(rec);
            }
          }
          await new Promise((resolve, reject) => {
            errorTx.oncomplete = resolve;
            errorTx.onerror = () => reject(errorTx.error);
          });
          return { status: 'error', code: 'SYNC_PUSH_FAILED', message: pushData.error || pushData.message || 'Push validation failed' };
        }

        // Handle backend-specific failed records
        if (pushData.failed && Array.isArray(pushData.failed)) {
          failedRecords = pushData.failed;
          console.log(`[Sync] Backend reported ${failedRecords.length} failed records`);
          const failedTx = db.transaction(STORE_NAME, 'readwrite');
          const failedStore = failedTx.objectStore(STORE_NAME);
          for (const failed of failedRecords) {
            const rec = await new Promise((resolve, reject) => {
              const req = failedStore.get(failed.id);
              req.onsuccess = () => resolve(req.result);
              req.onerror = () => reject(req.error);
            });
            if (rec) {
              rec.syncState = 'error';
              rec.syncError = failed.reason || 'Backend rejected';
              failedStore.put(rec);
            }
          }
          await new Promise((resolve, reject) => {
            failedTx.oncomplete = resolve;
            failedTx.onerror = () => reject(failedTx.error);
          });
        }

        pushedCount = recordsToPush.length;
        console.log(`[Sync] Push successful - ${pushedCount} records sent`);
      }

      // --- VERIFICATION PHASE ---
      console.log('[Sync] Beginning verification pull...');
      const verifyResponse = await fetchWithTimeout(syncUrl, {
        action: 'pull',
        passcode: sessionStorage.getItem('BPA_SESSION_PASSCODE') || ''
      });

      if (!verifyResponse.ok) {
        console.error('[Sync] Verification pull failed - HTTP error', verifyResponse.status);
        if (verifyResponse.status >= 500) return { status: 'error', code: 'SYNC_PULL_FAILED', message: 'Server error during verification. Will retry.' };
        throw new Error(`Verification pull failed with status: ${verifyResponse.status}`);
      }

      const verifyData = await verifyResponse.json();
      if (verifyData.ok === false || verifyData.success === false) {
        console.error('[Sync] Verification pull failed - backend error:', verifyData.error || verifyData.message);
        if (verifyData.code === 'INVALID_PASSCODE') return { status: 'error', code: 'SESSION_REQUIRED', message: 'Your session expired. Please log in again.' };
        throw new Error(verifyData.error || verifyData.message || 'Verification pull validation failed');
      }

      let verifyRecords = [];
      if (Array.isArray(verifyData)) {
        verifyRecords = verifyData;
      } else if (verifyData && (verifyData.success === true || verifyData.ok === true)) {
        if (Array.isArray(verifyData.data)) verifyRecords = verifyData.data;
        else if (Array.isArray(verifyData.records)) verifyRecords = verifyData.records;
        else if (Array.isArray(verifyData.pages)) verifyRecords = verifyData.pages;
      }

      if (!Array.isArray(verifyRecords)) {
        console.error('[Sync] Verification failed - no valid records array');
        throw new Error('Invalid verification response format');
      }

      verifiedCount = verifyRecords.length;
      console.log(`[Sync] Verification successful - ${verifiedCount} records confirmed`);

      // Update local sync status based on verification
      const verifyTx = db.transaction(STORE_NAME, 'readwrite');
      const verifyStore = verifyTx.objectStore(STORE_NAME);
      const allLocal = await new Promise((resolve, reject) => {
        const req = verifyStore.getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
      });

      const verifiedIds = new Set(verifyRecords.map(r => r.id));
      for (const local of allLocal) {
        if (verifiedIds.has(local.id)) {
          local.syncState = 'synced';
          local.lastSyncedAt = Date.now();
          local.syncError = '';
          verifyStore.put(local);
        } else {
          // Only mark as error if it was previously syncing or pending
          // Don't overwrite an existing error with a different message
          if (local.syncState === 'syncing') {
            local.syncState = 'error';
            local.syncError = 'Not returned by backend verification pull';
            verifyStore.put(local);
          }
        }
      }

      await new Promise((resolve, reject) => {
        verifyTx.oncomplete = resolve;
        verifyTx.onerror = () => reject(verifyTx.error);
      });

      // Final backup
      await this.backupToLocal();

      // Re-read fresh data to calculate unsynced count
      const freshData = await new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const req = tx.objectStore(STORE_NAME).getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
      });

      const unsyncedCount = freshData.filter(p =>
        p.syncState === 'pending' || p.syncState === 'syncing' || p.syncState === 'error'
      ).length;

      console.log(`[Sync] Sync complete. Unsynced count: ${unsyncedCount}`);

      if (unsyncedCount > 0) {
        return { status: 'error', message: `${unsyncedCount} files are not synced.`, pulledCount, pushedCount, verifiedCount, failedRecords };
      }

      return { status: 'synced', pulledCount, pushedCount, verifiedCount, failedRecords };

    } catch (error) {
      console.error('[Sync] Sync failed with error:', error.message);
      if (error.name === 'AbortError') {
        return { status: 'error', code: 'NETWORK_ERROR', message: 'The request timed out. Sync will retry when the connection is restored.' };
      }
      return { status: 'error', code: 'NETWORK_ERROR', message: error.message };
    }
  }
};